import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Container,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
  Table,
} from "reactstrap";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import DismissiblePageTip from "../../Components/Common/DismissiblePageTip";
import PageHelpLink from "../../Components/Common/PageHelpLink";
import { helpUrlForRoute } from "../../constants/helpRouteMap";
import Select from "react-select";
import {
  bulkApplyContactTags,
  bulkDeleteInactiveContacts,
  bulkRestoreContacts,
  createSegment,
  downloadContactsExportCsv,
  fetchAllTags,
  getExportSession,
  getInactiveContacts,
  getInactiveContactsSummary,
  getInactiveLastBulkDelete,
  InactiveContact,
  InactiveContactsSummary,
  InactiveLastBulkDelete,
  SegmentRules,
  startContactsExport,
  Tag,
  updateInactiveListSettings,
} from "../../helpers/audience_helper";

const PER_PAGE = 25;
const DAY_PRESETS = [60, 90, 180] as const;
const EXPORT_POLL_MS = 2000;
const EXPORT_POLL_MAX = 90;

/**
 * Contatos sem interação — higiene de lista (DOC 03 § 4.15 · LAC-128/129).
 * Por quê: Carla limpa base fria sem Postman; ≠ win-back (não dispara e-mail).
 */
const InactiveContactsPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const locale = i18n.language || "pt-BR";

  document.title = t("inactiveContacts.documentTitle");

  const [days, setDays] = useState(90);
  const [summary, setSummary] = useState<InactiveContactsSummary | null>(null);
  const [items, setItems] = useState<InactiveContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);

  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"selection" | "all">("all");
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);

  const [exporting, setExporting] = useState(false);
  const [thresholdDraft, setThresholdDraft] = useState(20);
  const [savingThreshold, setSavingThreshold] = useState(false);
  const [lastBulk, setLastBulk] = useState<InactiveLastBulkDelete | null>(null);
  const [restoring, setRestoring] = useState(false);
  const [countApiMessages, setCountApiMessages] = useState(true);
  const [savingCountApi, setSavingCountApi] = useState(false);

  const [tags, setTags] = useState<Tag[]>([]);
  const [tagModalOpen, setTagModalOpen] = useState(false);
  const [tagModalMode, setTagModalMode] = useState<"selection" | "all">("selection");
  const [tagConfirmOpen, setTagConfirmOpen] = useState(false);
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
  const [applyingTags, setApplyingTags] = useState(false);

  const [segmentModalOpen, setSegmentModalOpen] = useState(false);
  const [segmentName, setSegmentName] = useState("");
  const [savingSegment, setSavingSegment] = useState(false);

  const formatDate = (iso: string | null | undefined): string => {
    if (!iso) return "—";
    try {
      return new Intl.DateTimeFormat(locale, {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }).format(new Date(iso));
    } catch {
      return iso;
    }
  };

  const loadLastBulk = useCallback(async () => {
    try {
      const res = await getInactiveLastBulkDelete();
      if (res?.success && res.data) {
        setLastBulk(res.data);
      } else {
        setLastBulk(null);
      }
    } catch {
      setLastBulk(null);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [sumRes, listRes] = await Promise.all([
        getInactiveContactsSummary({ days }),
        getInactiveContacts({ days, page, per_page: PER_PAGE }),
      ]);
      if (!sumRes?.success || !listRes?.success) {
        setError(t("inactiveContacts.error.load"));
        setItems([]);
        setSummary(null);
        return;
      }
      setSummary(sumRes.data);
      setThresholdDraft(sumRes.data.dashboard_threshold_percent ?? 20);
      setCountApiMessages(sumRes.data.inactive_count_api_messages ?? true);
      setItems(listRes.data || []);
      setTotal(listRes.meta?.total ?? 0);
      setLastPage(listRes.meta?.last_page ?? 1);
      setSelected(new Set());
    } catch (e: any) {
      setError(e?.message || t("inactiveContacts.error.load"));
      setItems([]);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [days, page, t]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadLastBulk();
  }, [loadLastBulk]);

  useEffect(() => {
    fetchAllTags()
      .then(setTags)
      .catch(() => setTags([]));
  }, []);

  const buildInactiveSegmentRules = (): SegmentRules => ({
    version: 1,
    match: "all",
    conditions: [
      { type: "status", op: "eq", value: "active" },
      { type: "engagement", event: "open", op: "not_in_last_days", days },
      { type: "engagement", event: "click", op: "not_in_last_days", days },
    ],
  });

  const saveCountApiMessages = async (next: boolean) => {
    setSavingCountApi(true);
    try {
      const res = await updateInactiveListSettings({
        inactive_count_api_messages: next,
      });
      if (!res?.success) {
        toast.error(t("inactiveContacts.error.countApi"));
        return;
      }
      setCountApiMessages(res.data.inactive_count_api_messages);
      toast.success(t("inactiveContacts.toast.countApiSaved"));
      await load();
    } catch (e: any) {
      toast.error(e?.message || t("inactiveContacts.error.countApi"));
    } finally {
      setSavingCountApi(false);
    }
  };

  const submitSaveSegment = async () => {
    const name = segmentName.trim();
    if (!name) {
      toast.error(t("inactiveContacts.segment.nameRequired"));
      return;
    }
    setSavingSegment(true);
    try {
      const res = await createSegment({
        name,
        rules: buildInactiveSegmentRules(),
      });
      if (!res?.success) {
        toast.error(t("inactiveContacts.segment.error"));
        return;
      }
      const segmentId = res.data?.id;
      setSegmentModalOpen(false);
      setSegmentName("");
      toast.success(
        <span>
          {t("inactiveContacts.segment.success")}{" "}
          {segmentId ? (
            <Link to={`/segments?edit=${segmentId}`} className="text-white text-decoration-underline">
              {t("inactiveContacts.segment.successLink")}
            </Link>
          ) : null}
        </span>
      );
      if (segmentId) {
        navigate(`/segments?edit=${segmentId}`);
      }
    } catch (e: any) {
      toast.error(e?.message || t("inactiveContacts.segment.error"));
    } finally {
      setSavingSegment(false);
    }
  };

  const requestApplyTags = () => {
    if (selectedTagIds.length === 0) {
      toast.error(t("inactiveContacts.tag.pickOne"));
      return;
    }
    // Elegíveis totais exigem confirmação explícita — evita tag acidental em massa.
    if (tagModalMode === "all") {
      setTagConfirmOpen(true);
      return;
    }
    void submitApplyTags();
  };

  const submitApplyTags = async () => {
    if (selectedTagIds.length === 0) {
      toast.error(t("inactiveContacts.tag.pickOne"));
      return;
    }
    setApplyingTags(true);
    try {
      const payload =
        tagModalMode === "all"
          ? {
              filter: { inactive_days: days },
              tag_ids: selectedTagIds,
              mode: "append" as const,
            }
          : {
              contact_ids: Array.from(selected),
              tag_ids: selectedTagIds,
              mode: "append" as const,
            };
      const res = await bulkApplyContactTags(payload);
      if (!res?.success) {
        toast.error(t("inactiveContacts.error.tag"));
        return;
      }
      toast.success(
        t("inactiveContacts.toast.tagged", {
          count: res.data?.tagged_count ?? selected.size,
        })
      );
      setTagModalOpen(false);
      setTagConfirmOpen(false);
      setSelectedTagIds([]);
      await load();
    } catch (e: any) {
      toast.error(e?.message || t("inactiveContacts.error.tag"));
    } finally {
      setApplyingTags(false);
    }
  };

  const expectedConfirm =
    modalMode === "all"
      ? String(summary?.eligible_count ?? 0)
      : String(selected.size);

  const previewEmails = (summary?.preview_emails || []).slice(0, 10);

  const openModal = (mode: "selection" | "all") => {
    setModalMode(mode);
    setConfirmText("");
    setModalOpen(true);
  };

  const toggleOne = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const togglePage = () => {
    const pageIds = items.map((i) => i.id);
    const allSelected = pageIds.every((id) => selected.has(id));
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        pageIds.forEach((id) => next.delete(id));
      } else {
        pageIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const submitDelete = async () => {
    if (confirmText.trim() !== expectedConfirm) {
      toast.error(t("inactiveContacts.modal.confirmMismatch"));
      return;
    }
    setDeleting(true);
    try {
      const payload =
        modalMode === "all"
          ? { days, confirm_count: summary?.eligible_count ?? 0 }
          : { days, contact_ids: Array.from(selected) };
      const res = await bulkDeleteInactiveContacts(payload);
      if (!res?.success) {
        toast.error(t("inactiveContacts.error.delete"));
        return;
      }
      const queued = res.data?.job_status === "queued";
      toast.success(
        queued
          ? t("inactiveContacts.toast.queued")
          : t("inactiveContacts.toast.deleted", {
              count: res.data?.deleted_count ?? selected.size,
            })
      );
      setModalOpen(false);
      setPage(1);
      await load();
      await loadLastBulk();
    } catch (e: any) {
      toast.error(e?.message || t("inactiveContacts.error.delete"));
    } finally {
      setDeleting(false);
    }
  };

  /** Export CSV dos elegíveis — Job + poll (mesmo padrão da lista de contatos). */
  const runExport = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const start = await startContactsExport({
        mode: "inactive_hygiene",
        days,
      });
      if (!start?.success || !start.data?.id) {
        toast.error(t("inactiveContacts.error.export"));
        return;
      }
      toast.info(start.message || t("inactiveContacts.toast.exportQueued"), {
        autoClose: 3000,
      });

      const sessionId = start.data.id;
      let ready = false;
      for (let i = 0; i < EXPORT_POLL_MAX; i++) {
        await new Promise((r) => setTimeout(r, EXPORT_POLL_MS));
        const prog = await getExportSession(sessionId);
        if (!prog?.success || !prog.data) {
          toast.error(t("inactiveContacts.error.export"));
          return;
        }
        const status = prog.data.status;
        if (status === "failed") {
          toast.error(
            prog.data.failure_message || t("inactiveContacts.error.export")
          );
          return;
        }
        if (status === "completed" || prog.data.download_ready) {
          ready = true;
          break;
        }
      }
      if (!ready) {
        toast.error(t("inactiveContacts.error.exportTimeout"));
        return;
      }
      const today = new Date().toISOString().slice(0, 10);
      await downloadContactsExportCsv(
        sessionId,
        `contatos_sem_interacao_${days}d_${today}.csv`
      );
      toast.success(t("inactiveContacts.toast.exportDownloaded"));
    } catch (e: any) {
      toast.error(e?.message || t("inactiveContacts.error.export"));
    } finally {
      setExporting(false);
    }
  };

  const saveThreshold = async () => {
    const value = Math.max(5, Math.min(80, Math.round(Number(thresholdDraft))));
    if (Number.isNaN(value)) {
      toast.error(t("inactiveContacts.threshold.invalid"));
      return;
    }
    setSavingThreshold(true);
    try {
      const res = await updateInactiveListSettings({
        dashboard_threshold_percent: value,
      });
      if (!res?.success) {
        toast.error(t("inactiveContacts.error.threshold"));
        return;
      }
      setThresholdDraft(res.data.dashboard_threshold_percent);
      setSummary((prev) =>
        prev
          ? {
              ...prev,
              dashboard_threshold_percent: res.data.dashboard_threshold_percent,
            }
          : prev
      );
      toast.success(t("inactiveContacts.toast.thresholdSaved"));
    } catch (e: any) {
      toast.error(e?.message || t("inactiveContacts.error.threshold"));
    } finally {
      setSavingThreshold(false);
    }
  };

  const restoreLastBulk = async () => {
    if (!lastBulk || lastBulk.count <= 0 || lastBulk.contact_ids.length === 0) {
      return;
    }
    setRestoring(true);
    try {
      const res = await bulkRestoreContacts({
        contact_ids: lastBulk.contact_ids,
      });
      if (!res?.success) {
        toast.error(t("inactiveContacts.error.restore"));
        return;
      }
      toast.success(
        t("inactiveContacts.toast.restored", {
          count: res.data?.restored_count ?? lastBulk.count,
        })
      );
      await load();
      await loadLastBulk();
    } catch (e: any) {
      toast.error(e?.message || t("inactiveContacts.error.restore"));
    } finally {
      setRestoring(false);
    }
  };

  const neverSentEmpty =
    !loading &&
    summary &&
    summary.active_count > 0 &&
    summary.eligible_count === 0 &&
    total === 0;

  const helpHref = helpUrlForRoute("/contacts/inactive", locale);
  const canRestoreLast =
    !!lastBulk && lastBulk.count > 0 && lastBulk.contact_ids.length > 0;

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={t("inactiveContacts.title")}
          actions={
            <PageHelpLink href={helpHref} label={t("inactiveContacts.help")} />
          }
        />
        <ToastContainer closeButton={false} limit={3} />

        <DismissiblePageTip tipId="inactive-contacts-v1">
          {t("inactiveContacts.tip")}
        </DismissiblePageTip>

        <Card>
          <CardBody>
            <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-3">
              <div>
                <p className="text-muted mb-0 small">
                  {t("inactiveContacts.subtitle")}
                </p>
              </div>
              <div className="d-flex flex-wrap align-items-center gap-2">
                <Label className="mb-0 small text-muted" for="inactive-days">
                  {t("inactiveContacts.days.label")}
                </Label>
                <Input
                  id="inactive-days"
                  type="select"
                  bsSize="sm"
                  style={{ width: "auto" }}
                  value={days}
                  onChange={(e) => {
                    setPage(1);
                    setDays(Number(e.target.value));
                  }}
                >
                  {DAY_PRESETS.map((d) => (
                    <option key={d} value={d}>
                      {t("inactiveContacts.days.option", { days: d })}
                    </option>
                  ))}
                </Input>
              </div>
            </div>

            {summary && !loading && (
              <Alert color="info" className="mb-3">
                {t("inactiveContacts.summary", {
                  eligible: summary.eligible_count,
                  percent: summary.eligible_percent,
                  active: summary.active_count,
                  days: summary.days,
                })}
              </Alert>
            )}

            {error && (
              <Alert color="danger" className="mb-3">
                {error}
              </Alert>
            )}

            {/* Limiar do card no dashboard — evita aviso cedo demais ou tarde demais. */}
            <div className="d-flex flex-wrap align-items-end gap-2 mb-3">
              <div>
                <Label className="mb-1 small text-muted" for="inactive-threshold">
                  {t("inactiveContacts.threshold.label")}
                </Label>
                <Input
                  id="inactive-threshold"
                  type="number"
                  bsSize="sm"
                  min={5}
                  max={80}
                  style={{ width: 88 }}
                  value={thresholdDraft}
                  disabled={loading || savingThreshold}
                  onChange={(e) => setThresholdDraft(Number(e.target.value))}
                />
              </div>
              <Button
                color="soft-primary"
                size="sm"
                disabled={loading || savingThreshold}
                onClick={saveThreshold}
              >
                {savingThreshold ? (
                  <Spinner size="sm" />
                ) : (
                  t("inactiveContacts.threshold.save")
                )}
              </Button>
              <span className="text-muted small">
                {t("inactiveContacts.threshold.hint", {
                  current: summary?.dashboard_threshold_percent ?? thresholdDraft,
                })}
              </span>
            </div>

            <div className="mb-3">
              <div className="form-check">
                <Input
                  id="inactive-count-api"
                  type="checkbox"
                  className="form-check-input"
                  checked={countApiMessages}
                  disabled={loading || savingCountApi}
                  onChange={(e) => void saveCountApiMessages(e.target.checked)}
                />
                <Label htmlFor="inactive-count-api" className="form-check-label">
                  {t("inactiveContacts.countApi.label")}
                </Label>
              </div>
              <div className="form-text">{t("inactiveContacts.countApi.hint")}</div>
            </div>

            <div className="d-flex flex-wrap gap-2 mb-3">
              <Button
                color="danger"
                size="sm"
                disabled={selected.size === 0 || loading}
                onClick={() => openModal("selection")}
              >
                {t("inactiveContacts.actions.deleteSelected", {
                  count: selected.size,
                })}
              </Button>
              <Button
                color="soft-danger"
                size="sm"
                disabled={
                  loading || !summary || summary.eligible_count === 0
                }
                onClick={() => openModal("all")}
              >
                {t("inactiveContacts.actions.deleteAll", {
                  count: summary?.eligible_count ?? 0,
                })}
              </Button>
              <Button
                color="soft-secondary"
                size="sm"
                disabled={
                  loading || exporting || !summary || summary.eligible_count === 0
                }
                onClick={runExport}
              >
                {exporting ? (
                  <Spinner size="sm" />
                ) : (
                  t("inactiveContacts.actions.exportCsv")
                )}
              </Button>
              <Button
                color="soft-primary"
                size="sm"
                disabled={loading || !summary || summary.eligible_count === 0}
                onClick={() => {
                  setSegmentName(
                    t("inactiveContacts.segment.defaultName", { days })
                  );
                  setSegmentModalOpen(true);
                }}
              >
                {t("inactiveContacts.actions.saveSegment")}
              </Button>
              <Button
                color="soft-info"
                size="sm"
                disabled={
                  loading ||
                  tags.length === 0 ||
                  (selected.size === 0 &&
                    (!summary || summary.eligible_count === 0))
                }
                onClick={() => {
                  setTagModalMode(selected.size > 0 ? "selection" : "all");
                  setSelectedTagIds([]);
                  setTagModalOpen(true);
                }}
              >
                {t("inactiveContacts.actions.applyTag")}
              </Button>
              {canRestoreLast && (
                <Button
                  color="soft-success"
                  size="sm"
                  disabled={loading || restoring}
                  onClick={restoreLastBulk}
                >
                  {restoring ? (
                    <Spinner size="sm" />
                  ) : (
                    t("inactiveContacts.actions.restoreLast", {
                      count: lastBulk!.count,
                    })
                  )}
                </Button>
              )}
              <Link
                to="/contacts?trashed=only"
                className="btn btn-sm btn-soft-secondary"
              >
                {t("inactiveContacts.actions.trash")}
              </Link>
            </div>

            {loading ? (
              <div className="text-center py-5">
                <Spinner color="primary" />
              </div>
            ) : neverSentEmpty ? (
              <Alert color="light" className="border text-center py-4 mb-0">
                <p className="mb-2">{t("inactiveContacts.empty.neverSent")}</p>
                <Link to="/campaigns" className="btn btn-sm btn-primary">
                  {t("inactiveContacts.empty.ctaCampaigns")}
                </Link>
              </Alert>
            ) : items.length === 0 ? (
              <Alert color="success" className="mb-0">
                {t("inactiveContacts.empty.healthy")}
              </Alert>
            ) : (
              <>
                <div className="table-responsive">
                  <Table className="align-middle table-nowrap mb-0">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: 40 }}>
                          <Input
                            type="checkbox"
                            checked={
                              items.length > 0 &&
                              items.every((i) => selected.has(i.id))
                            }
                            onChange={togglePage}
                            aria-label={t("inactiveContacts.table.selectPage")}
                          />
                        </th>
                        <th>{t("inactiveContacts.table.email")}</th>
                        <th>{t("inactiveContacts.table.name")}</th>
                        <th>{t("inactiveContacts.table.lastSent")}</th>
                        <th>{t("inactiveContacts.table.tags")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((row) => (
                        <tr key={row.id}>
                          <td>
                            <Input
                              type="checkbox"
                              checked={selected.has(row.id)}
                              onChange={() => toggleOne(row.id)}
                              aria-label={row.email}
                            />
                          </td>
                          <td>{row.email}</td>
                          <td>{row.name || "—"}</td>
                          <td>{formatDate(row.last_sent_at)}</td>
                          <td>
                            {(row.tags || []).length === 0
                              ? "—"
                              : (row.tags || []).map((tag) => (
                                  <Badge
                                    key={tag.id}
                                    color="secondary"
                                    className="me-1"
                                  >
                                    {tag.name}
                                  </Badge>
                                ))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-3">
                  <span className="text-muted small">
                    {t("inactiveContacts.pagination.label", {
                      page,
                      lastPage,
                      total,
                    })}
                  </span>
                  <div className="d-flex gap-2">
                    <Button
                      color="light"
                      size="sm"
                      disabled={page <= 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                      {t("inactiveContacts.pagination.prev")}
                    </Button>
                    <Button
                      color="light"
                      size="sm"
                      disabled={page >= lastPage}
                      onClick={() => setPage((p) => p + 1)}
                    >
                      {t("inactiveContacts.pagination.next")}
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardBody>
        </Card>

        <Modal isOpen={modalOpen} toggle={() => !deleting && setModalOpen(false)}>
          <ModalHeader toggle={() => !deleting && setModalOpen(false)}>
            {t("inactiveContacts.modal.title")}
          </ModalHeader>
          <ModalBody>
            <p>
              {modalMode === "all"
                ? t("inactiveContacts.modal.bodyAll", {
                    count: summary?.eligible_count ?? 0,
                    days,
                  })
                : t("inactiveContacts.modal.bodySelected", {
                    count: selected.size,
                  })}
            </p>
            {/* Preview reduz risco de exclusão “às cegas” (U1). */}
            {previewEmails.length > 0 && (
              <div className="mb-3">
                <p className="small text-muted mb-1">
                  {t("inactiveContacts.modal.previewLabel")}
                </p>
                <ul className="small mb-0 ps-3">
                  {previewEmails.map((email) => (
                    <li key={email}>{email}</li>
                  ))}
                </ul>
              </div>
            )}
            <p className="text-muted small">
              {t("inactiveContacts.modal.disclaimer")}
            </p>
            <Label for="inactive-confirm">
              {t("inactiveContacts.modal.confirmLabel", {
                count: expectedConfirm,
              })}
            </Label>
            <Input
              id="inactive-confirm"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              disabled={deleting}
              autoComplete="off"
            />
          </ModalBody>
          <ModalFooter>
            <Button
              color="light"
              disabled={deleting}
              onClick={() => setModalOpen(false)}
            >
              {t("inactiveContacts.modal.cancel")}
            </Button>
            <Button
              color="danger"
              disabled={deleting || confirmText.trim() !== expectedConfirm}
              onClick={submitDelete}
            >
              {deleting ? <Spinner size="sm" /> : t("inactiveContacts.modal.submit")}
            </Button>
          </ModalFooter>
        </Modal>

        <Modal
          isOpen={segmentModalOpen}
          toggle={() => !savingSegment && setSegmentModalOpen(false)}
        >
          <ModalHeader toggle={() => !savingSegment && setSegmentModalOpen(false)}>
            {t("inactiveContacts.segment.title")}
          </ModalHeader>
          <ModalBody>
            <p className="text-muted small">{t("inactiveContacts.segment.body", { days })}</p>
            <Alert color="warning" className="py-2 small">
              {t("inactiveContacts.segment.receivedNote")}
            </Alert>
            <Label for="inactive-segment-name">{t("inactiveContacts.segment.nameLabel")}</Label>
            <Input
              id="inactive-segment-name"
              value={segmentName}
              onChange={(e) => setSegmentName(e.target.value)}
              disabled={savingSegment}
            />
          </ModalBody>
          <ModalFooter>
            <Button
              color="light"
              disabled={savingSegment}
              onClick={() => setSegmentModalOpen(false)}
            >
              {t("inactiveContacts.modal.cancel")}
            </Button>
            <Button color="primary" disabled={savingSegment} onClick={submitSaveSegment}>
              {savingSegment ? <Spinner size="sm" /> : t("inactiveContacts.segment.submit")}
            </Button>
          </ModalFooter>
        </Modal>

        <Modal
          isOpen={tagModalOpen}
          toggle={() => !applyingTags && setTagModalOpen(false)}
        >
          <ModalHeader toggle={() => !applyingTags && setTagModalOpen(false)}>
            {t("inactiveContacts.tag.title")}
          </ModalHeader>
          <ModalBody>
            <p className="text-muted small">
              {tagModalMode === "all"
                ? t("inactiveContacts.tag.bodyAll", {
                    count: summary?.eligible_count ?? 0,
                  })
                : t("inactiveContacts.tag.bodySelected", {
                    count: selected.size,
                  })}
            </p>
            <Label>{t("inactiveContacts.tag.pickLabel")}</Label>
            <Select
              isMulti
              options={tags.map((tag) => ({ value: tag.id, label: tag.name }))}
              value={tags
                .filter((tag) => selectedTagIds.includes(tag.id))
                .map((tag) => ({ value: tag.id, label: tag.name }))}
              onChange={(
                opts: readonly { value: number; label: string }[] | null
              ) => setSelectedTagIds((opts || []).map((o) => o.value))}
              placeholder={t("inactiveContacts.tag.pickPlaceholder")}
              isDisabled={applyingTags}
            />
          </ModalBody>
          <ModalFooter>
            <Button
              color="light"
              disabled={applyingTags}
              onClick={() => setTagModalOpen(false)}
            >
              {t("inactiveContacts.modal.cancel")}
            </Button>
            <Button color="primary" disabled={applyingTags} onClick={requestApplyTags}>
              {applyingTags ? <Spinner size="sm" /> : t("inactiveContacts.tag.submit")}
            </Button>
          </ModalFooter>
        </Modal>

        <Modal
          isOpen={tagConfirmOpen}
          toggle={() => !applyingTags && setTagConfirmOpen(false)}
        >
          <ModalHeader toggle={() => !applyingTags && setTagConfirmOpen(false)}>
            {t("inactiveContacts.tag.confirmAllTitle")}
          </ModalHeader>
          <ModalBody>
            <p className="mb-0">
              {t("inactiveContacts.tag.confirmAllBody", {
                count: summary?.eligible_count ?? 0,
              })}
            </p>
          </ModalBody>
          <ModalFooter>
            <Button
              color="light"
              disabled={applyingTags}
              onClick={() => setTagConfirmOpen(false)}
            >
              {t("inactiveContacts.modal.cancel")}
            </Button>
            <Button color="primary" disabled={applyingTags} onClick={submitApplyTags}>
              {applyingTags ? (
                <Spinner size="sm" />
              ) : (
                t("inactiveContacts.tag.confirmAllSubmit")
              )}
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
    </div>
  );
};

export default InactiveContactsPage;
